import { useCallback, useEffect, useMemo, useState } from "react";
import { useRecoilState } from "recoil";
import {
  currentNewRequisition,
  currentNewRequisitionEdit,
  providerProfileState,
} from "../../recoil/atoms";
import { NewRequisitionForm, UserProviderProfile } from "../../recoil/types";
import { GetProfile } from "../Profile/Services/UpdateProfileInfoService";
import {
  GetAllNewReqLimsAccount,
  GetAllNewReqOrderingProviders,
} from "./Services/NewReqService";
import { NewReqClinicAccount, NewReqOrderingProvider } from "./types";
import { getInternationalBillToReset } from "./helpers";
import { formatOrderingProvider } from "./Services/helpers";
import { getClinicChangeBlocker } from "./orderEdit";
import { useAuthenticationHook } from "../../utils/hooks/useAuthenticationHook";

type UseClinicInformationArgs = {
  preflight: boolean;
  isError?: boolean;
  isEditMode?: boolean;
  submittedRequisition?: NewRequisitionForm;
};

const getClinicAccountPatch = (
  clinic: NewReqClinicAccount,
  isEditMode = false,
): Partial<NewRequisitionForm> => ({
  clinicAccountNumber: clinic.lims_id ?? "",
  clinicFax: clinic.office_fax ?? "",
  clinicName: clinic.sfname ?? "",
  clinicPhone: clinic.phone ?? "",
  isStandardAccount: clinic.is_standard_account,
  isMobilePhlebotomyAccount: clinic.is_mobile_phlebotomy_account,
  isInternationalAccount: clinic.is_international_account,
  isPatientSheetAccount: clinic.is_patient_sheet_account,
  isInsuranceSheetAccount: clinic.is_insurance_sheet_account,
  isFetalAntigenAccount: clinic.is_fetal_antigen_account,
  isFragileXAccount: clinic.is_fragile_x_account,
  isRhdOnlyAccount: clinic.is_rhd_only_account,
  is130PanelAccount: clinic.is_130_panel_account,
  is611PanelAccount: clinic.is_611_panel_account,
  is113CustomPanelAccount: clinic.is_113_custom_panel_account,
  // Edits keep the submitted services and billing.
  ...(!isEditMode && {
    isMobilePhlebotomy: false,
    ...getInternationalBillToReset(!!clinic.is_international_account),
  }),
});

export const useClinicInformation = ({
  preflight,
  isError,
  isEditMode = false,
  submittedRequisition,
}: UseClinicInformationArgs) => {
  const { auth, setLoggedOut } = useAuthenticationHook();

  const [newGlobalRequisition, setNewGlobalRequisition] = useRecoilState(
    currentNewRequisition,
  );
  const [newRequisitionEdit, setNewRequisitionEdit] = useRecoilState(
    currentNewRequisitionEdit,
  );

  const [newRequisition, setNewRequisition] = useState<NewRequisitionForm>(
    () => submittedRequisition || newGlobalRequisition,
  );
  const activeRequisition = isEditMode
    ? newRequisitionEdit.requisitionForm
    : newRequisition;
  const effectiveIsError = Boolean(
    isError || (isEditMode && newRequisitionEdit.isError),
  );

  const setActiveRequisition = useCallback(
    (updates: Partial<NewRequisitionForm>) => {
      if (isEditMode) {
        setNewRequisitionEdit((currentRequisition) => ({
          ...currentRequisition,
          requisitionForm: {
            ...currentRequisition.requisitionForm,
            ...updates,
          },
        }));
        return;
      }
      setNewRequisition((currentRequisition) => ({
        ...currentRequisition,
        ...updates,
      }));
    },
    [isEditMode, setNewRequisitionEdit],
  );

  useEffect(() => {
    if (!preflight && !isEditMode) {
      setNewGlobalRequisition((currentRequisition) => ({
        ...currentRequisition,
        ...newRequisition,
      }));
    }
  }, [isEditMode, newRequisition, preflight, setNewGlobalRequisition]);

  const [clinicAccounts, setClinicAccounts] = useState<NewReqClinicAccount[]>(
    [],
  );

  useEffect(() => {
    const getAllClinicAccounts = async () => {
      if (auth && !clinicAccounts.length && !preflight) {
        const allClinicAccounts = await GetAllNewReqLimsAccount(
          auth!.token,
          setLoggedOut,
        );
        setClinicAccounts(allClinicAccounts);
      }
    };
    getAllClinicAccounts();
  }, [auth, preflight]);

  const [profileInfo, setProfile] = useRecoilState(providerProfileState);

  const [orderingProviders, setOrderingProviders] = useState<
    NewReqOrderingProvider[]
  >([]);
  const [
    filteredFormattedOrderingProviders,
    setFilteredFormattedOrderingProviders,
  ] = useState<string[]>([]);

  const [isNpiError, setIsNpiError] = useState<boolean>(false);

  useEffect(() => {
    const getOrderingProviders = async () => {
      if (auth && !orderingProviders.length && !preflight) {
        const allOrderingProviders = await GetAllNewReqOrderingProviders(
          auth!.token,
          setLoggedOut,
        );
        setOrderingProviders(allOrderingProviders);
      }
    };
    getOrderingProviders();
  }, [auth, preflight]);

  const filteredOrderingProviders = useMemo(
    () =>
      activeRequisition.clinicAccountNumber
        ? orderingProviders.filter(
            (provider) =>
              provider?.lims_id === activeRequisition.clinicAccountNumber,
          )
        : orderingProviders,
    [activeRequisition.clinicAccountNumber, orderingProviders],
  );

  useEffect(() => {
    const formattedOrderingProviders = filteredOrderingProviders.map(
      (provider) =>
        formatOrderingProvider(provider.physician_name, provider.npi),
    );
    setFilteredFormattedOrderingProviders(formattedOrderingProviders);
  }, [filteredOrderingProviders]);

  useEffect(() => {
    const getFullProfileInfo = async () => {
      if (auth && !profileInfo && clinicAccounts.length) {
        const profileResponse = await GetProfile(auth.token, setLoggedOut);
        if (profileResponse?.ok && clinicAccounts.length) {
          const profileData =
            (await profileResponse.json()) as UserProviderProfile;
          setProfile(profileData);
          const initialClinic =
            clinicAccounts.find(
              (account) => account?.lims_id === profileData.account?.lims_id,
            ) || clinicAccounts[0];
          if (
            !isEditMode &&
            !activeRequisition.clinicAccountNumber &&
            !activeRequisition.clinicName
          ) {
            setActiveRequisition(getClinicAccountPatch(initialClinic));
          }
        }
      } else if (
        profileInfo &&
        !isEditMode &&
        !activeRequisition.clinicAccountNumber &&
        !activeRequisition.clinicName &&
        clinicAccounts.length
      ) {
        const initialClinic =
          clinicAccounts.find(
            (account) => account?.lims_id === profileInfo.account?.lims_id,
          ) || clinicAccounts[0];
        setActiveRequisition(getClinicAccountPatch(initialClinic));
      }
    };
    getFullProfileInfo();
  }, [
    activeRequisition.clinicAccountNumber,
    activeRequisition.clinicName,
    clinicAccounts,
    isEditMode,
    profileInfo,
    setActiveRequisition,
  ]);

  const isLoading =
    !preflight &&
    !profileInfo &&
    !clinicAccounts.length &&
    !filteredFormattedOrderingProviders.length;

  const submittedClinic = clinicAccounts.find(
    (clinic) => clinic.lims_id === submittedRequisition?.clinicAccountNumber,
  );
  const isInternationalOrder = submittedClinic?.is_international_account;

  useEffect(() => {
    if (isEditMode && isInternationalOrder !== undefined) {
      setActiveRequisition({ isInternationalAccount: isInternationalOrder });
    }
  }, [isEditMode, isInternationalOrder, setActiveRequisition]);

  const clinicAutocompleteOptions = useMemo(() => {
    const editedOrder = isEditMode &&
      submittedRequisition && {
        ...submittedRequisition,
        isInternationalAccount: !!isInternationalOrder,
      };
    return clinicAccounts.map((clinic) => ({
      id: clinic.lims_id,
      label: clinic.sfname,
      disabledReason: editedOrder
        ? getClinicChangeBlocker(
            editedOrder,
            getClinicAccountPatch(clinic, true),
          )
        : null,
    }));
  }, [clinicAccounts, isEditMode, isInternationalOrder, submittedRequisition]);

  const onClinicChange = useCallback(
    (val: string | boolean) => {
      const selectedClinic = clinicAccounts.find(
        (clinic) => clinic.lims_id === val,
      );
      if (!selectedClinic) {
        return;
      }
      setActiveRequisition({
        ...getClinicAccountPatch(selectedClinic, isEditMode),
        ...(!isEditMode && { expandedCarrierPanel: null }),
        orderingHealthcareProviders: "",
        orderingHealthcareNPI: "",
        orderingHealthcareProviderFormatted: "",
      });
    },
    [clinicAccounts, isEditMode, setActiveRequisition],
  );

  const onOrderingProviderChange = useCallback(
    (val: string | boolean) => {
      const value = String(val);
      const healthcareProvider = value.split("- NPI:");
      if (
        filteredFormattedOrderingProviders.length &&
        !filteredFormattedOrderingProviders.includes(value)
      ) {
        setIsNpiError(true);
        return;
      }
      setIsNpiError(false);
      setActiveRequisition({
        orderingHealthcareProviders: healthcareProvider[0]?.trim(),
        orderingHealthcareNPI: healthcareProvider[1]?.trim(),
        orderingHealthcareProviderFormatted: value,
      });
    },
    [filteredFormattedOrderingProviders, setActiveRequisition],
  );

  return {
    activeRequisition,
    clinicAutocompleteOptions,
    effectiveIsError,
    filteredFormattedOrderingProviders,
    isLoading,
    isNpiError,
    onClinicChange,
    onOrderingProviderChange,
  };
};
